import React, { useMemo, useState } from 'react';
import type { Driver } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useDriverDrawerStore } from '@hero/hooks-shared';
import { useDriverApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { PencilIcon, VehicleMainIcon } from '@hero/web-icons';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import type { ActionButtonGroup } from '../../../app/ActionButton/ActionButton';
import { DriverSummaryTab } from './DriverSummaryTab';
import { DriverOrdersTab } from './DriverOrdersTab';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { DrawerFooter } from '../../../app/Drawer/DrawerFooter';
import { DriverAssignVehicleModal } from './DriverAssignVehicleModal';
import { MemberEditModal } from '../../User/Modal/MemberEditModal';

export interface DriverDrawerProps {
  syncUrl?: boolean | string;
}

interface DriverDrawerContentProps {
  driver: Driver;
  onClose: () => void;
}

const DriverDrawerContent: React.FC<DriverDrawerContentProps> = ({ driver, onClose }) => {
  const { t } = useTranslation(['m-driver']);
  const { isEnabled: canEditDriver } = useFeature('driver_module', 'sub_edit_driver');
  const [activeTab, setActiveTab] = useState('summary');
  const [assignVehicleOpen, setAssignVehicleOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const tabs = [
    { id: 'summary', label: t('drawer.tabs.summary') },
    { id: 'orders', label: t('drawer.tabs.orders') },
  ];

  const actions: ActionButtonGroup[] = useMemo(
    () => [
      {
        id: 'assign-vehicle',
        label: t('drawer.actions.assign_vehicle'),
        description: t('drawer.action_assign_vehicle_description'),
        icon: <VehicleMainIcon className="w-4 h-4" />,
        onClick: () => setAssignVehicleOpen(true),
      },
      {
        id: 'edit',
        label: t('drawer.actions.edit'),
        description: t('drawer.action_edit_driver_description'),
        icon: <PencilIcon className="w-4 h-4" />,
        onClick: () => setEditModalOpen(true),
        hide: !canEditDriver,
      },
    ],
    [t, canEditDriver],
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return <DriverSummaryTab driver={driver} />;
      case 'orders':
        return <DriverOrdersTab driver={driver} />;
      default:
        return null;
    }
  };

  return (
    <>
      <DrawerHeader title={driver.name || ''} subtitle={driver.phone} onClose={onClose} />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>{renderTab()}</DrawerContent>
      <DrawerFooter actions={actions} buttonLabel={t('table.columns.actions')} />

      <DriverAssignVehicleModal
        isOpen={assignVehicleOpen}
        onClose={() => setAssignVehicleOpen(false)}
        driver={driver}
      />
      <MemberEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        memberId={driver._id}
        displayForUser="carrier"
      />
    </>
  );
};

export const DriverDrawer: React.FC<DriverDrawerProps> = ({ syncUrl }) => (
  <StackedDrawer<Driver>
    useStore={useDriverDrawerStore}
    useItemApi={id => useDriverApi(id)}
    syncUrl={syncUrl}
    defaultParamKey="driverId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="Driver Details"
    description="View driver information"
    renderContent={(driver, onClose) => <DriverDrawerContent driver={driver} onClose={onClose} />}
  />
);
